import { getSearchGiphys, getTrendingGiphys } from 'api/giphy'
import classnames from 'classnames'
import { SearchLabel } from 'components/Accessibility'
import { Close, Gif } from 'components/lib/icons'
import Input from 'components/lib/Input'
import useEmbedLocale from 'hooks/useEmbedLocale'
import useInputValue from 'hooks/useInputValue'
import useIsMobile from 'hooks/useIsMobile'
import useKeyPress from 'hooks/useKeyPress'
import giphySrc from 'images/giphy.png'
import { pathOr } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GiphyGIF } from 'types/giphy'
import useOnClickOutside from 'use-onclickoutside'
import { debounce } from 'utils'
import resetFocus from 'utils/resetFocus'

import GiphyPreview from './GiphyPreview'

import './style.css'

const GIPHY_LIMIT = 51 // based on columns per row displayed
interface GiphyPickerProps {
  onAddGiphy: Function,
  disabled?: boolean
}

function filterBrokenGifLinks(giphys) {
  return giphys.filter(function filterBrokenGif(giphy) {
    const previewGifUrl = pathOr(null, ['images', 'preview_gif', 'url'], giphy)
    const originalStillUrl = pathOr(null, ['images', 'original_still', 'url'], giphy)

    return !!(previewGifUrl && originalStillUrl)
  })
}

const GiphyPicker = ({
  onAddGiphy,
  disabled = false
}: GiphyPickerProps) => {
  const { t } = useTranslation()
  const locale = useEmbedLocale()

  const [giphys, setGiphys] = useState<GiphyGIF[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [clickedOutside, setClickedOutside] = useState(false)
  const giphyRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const giphyButtonRef = useRef<HTMLButtonElement>(null)
  const { value, onChange } = useInputValue('')
  const escapePressed = useKeyPress({ targetKey: 'Escape' })
  const isMobile = useIsMobile()

  const search = useMemo(() => debounce(async (searchValue) => {
    const { data:giphyData } = await getSearchGiphys(searchValue, GIPHY_LIMIT, { locale })
    setGiphys(filterBrokenGifLinks(giphyData.data))
  }, 200, false), [locale])

  const searchTrending = useCallback(async () => {
    const { data:giphyData } = await getTrendingGiphys(GIPHY_LIMIT)
    setGiphys(filterBrokenGifLinks(giphyData.data))
  }, [])

  useOnClickOutside(giphyRef, () => {
    if (pickerOpen) {
      setPickerOpen(false)
      resetFocus(giphyButtonRef?.current)
      setClickedOutside(true)
    }
  })

  useEffect(() => {
    pickerOpen && searchTrending()
  }, [pickerOpen, searchTrending])

  useEffect(() => {
    if (pickerOpen && escapePressed) {
      setPickerOpen(false)
    }
  }, [escapePressed, pickerOpen])

  useEffect(() => {
    if (pickerOpen) {
      if (value.length > 0) {
        search(value)
      } else {
        searchTrending()
      }
    }
  }, [value, pickerOpen, search, searchTrending])

  useEffect(() => {
    if (!clickedOutside) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setClickedOutside(false)
    }, 200)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [clickedOutside])

  const addGiphyToMessage = useCallback(({ images, title = 'gif' }) => {
    onAddGiphy(`
      <img
        alt=${title}
        src="${images?.original?.url}"
      />
    `)
    setPickerOpen(false)
    resetFocus(giphyButtonRef?.current)
  }, [onAddGiphy])

  const onInputFocus = (event) => {
    // This prevents the global listener from resizing the frame in mobile
    if (isMobile) {
      event.stopPropagation()
    }
  }

  const openGiphy = (event) => {
    if (clickedOutside) {
      return event.preventDefault()
    }

    setPickerOpen(open => !open)
  }

  const clearInput = () => {
    onChange(null)
    searchInputRef?.current?.focus()
  }

  return (
    <>
      <button
        className="drift-widget-composer-giphy-toggle"
        onClick={openGiphy}
        aria-label={`Click to ${pickerOpen ? 'close' : 'open'} GIF picker`}
        aria-pressed={pickerOpen}
        disabled={disabled}
      >
        <Gif />
      </button>

      <div
        aria-expanded={pickerOpen}
        tabIndex={-1}
        className={classnames("drift-widget-composer-giphy-picker", {
          "drift-widget-composer-giphy-picker--show": pickerOpen,
          'drift-widget-composer-giphy-picker--mobile': isMobile
        })}
        ref={giphyRef}
      >
        <div className="drift-widget-composer-giphy-picker-search">
          <SearchLabel
            ariaLabel="Search for a GIF"
            ariaLabelledBy="searchGIF"
            className="drift-widget--giphy-picker-search-label"
            htmlFor="giphy-picker-search"
          />
          <Input
            type="search"
            placeholder={t('gifPicker.searchPlaceholder')}
            value={value}
            onChange={onChange}
            onFocus={onInputFocus}
            aria-label="Search GIFs"
            aria-live="polite"
            id="giphy-picker-search"
            shouldFocus={pickerOpen && !isMobile}
          />
          {!!value && (
            <button
              aria-label="Clear GIF search field"
              className="drift-widget--giphy-picker-search-clear"
              onClick={clearInput}
            >
              <Close />
            </button>
          )}
        </div>
        <div className="drift-widget-composer-giphy-picker-list-wrapper">
          <ul className="drift-widget-composer-giphy-list">
            {giphys.map((giphy, idx) => (
              <GiphyPreview
                addGiphyToMessage={addGiphyToMessage}
                giphy={giphy}
                key={`${giphy.id}-${idx}`}
              />
            ))}
          </ul>
        </div>
        <span className="drift-widget-composer-giphy-picker-power">
          <img src={giphySrc} alt={t('gifPicker.poweredBy')} />
        </span>
      </div>
    </>
  )
}

export default GiphyPicker
